import { LoaderCircle, RefreshCcw, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, authConfig, formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/portalHelpers";

const panelClass = "rounded-[32px] border border-[#001935]/10 bg-white/90 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.08)] md:p-8";
const fieldClass =
  "w-full rounded-[20px] border border-[#001935]/10 bg-white px-4 py-3 text-sm text-[#001935] outline-none transition placeholder:text-[#8c8578] focus:border-[#D4AF37]/60 focus:ring-2 focus:ring-[#D4AF37]/20";

const makeForm = (article = null) => ({
  title: article?.title || "",
  excerpt: article?.excerpt || "",
  image_url: article?.image_url || "",
  executive_summary: article?.executive_summary || "",
  paragraphs: article?.paragraphs?.join("\n\n") || "",
  insight_points: article?.insight_points?.join("\n") || "",
  takeaway: article?.takeaway || "",
  advisory: article?.advisory || "",
  pull_quote: article?.pull_quote || "",
  hero_caption: article?.hero_caption || "",
  market_context: article?.market_context || "",
});

const splitLines = (value) =>
  value
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);

export const AdminMarketInsightsPanel = () => {
  const [articles, setArticles] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [form, setForm] = useState(makeForm());
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadArticles = useCallback(async () => {
    setLoadingList(true);
    try {
      const response = await api.get("/admin/market-insights", authConfig());
      setArticles(response.data);
      setSelectedId((current) => current || response.data[0]?.id || "");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      setArticles([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const loadDetail = useCallback(async (articleId) => {
    if (!articleId) {
      setSelectedArticle(null);
      setForm(makeForm());
      return;
    }

    setLoadingDetail(true);
    try {
      const response = await api.get(`/admin/market-insights/${articleId}`, authConfig());
      setSelectedArticle(response.data);
      setForm(makeForm(response.data));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      setSelectedArticle(null);
      setForm(makeForm());
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
    }
  }, [loadDetail, selectedId]);

  const draftStats = useMemo(
    () => ({
      total: articles.length,
      drafts: articles.filter((article) => article.status === "draft").length,
      published: articles.filter((article) => article.status === "published").length,
    }),
    [articles],
  );

  const syncLatestReleases = async () => {
    setSyncing(true);
    try {
      const response = await api.post("/admin/market-insights/sync-latest", {}, authConfig());
      toast.success(`${response.data.message}: ${response.data.draft_created_count} drafts created`);
      await loadArticles();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const saveChanges = async () => {
    if (!selectedArticle) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        paragraphs: splitLines(form.paragraphs),
        insight_points: splitLines(form.insight_points),
      };
      const response = await api.patch(`/admin/market-insights/${selectedArticle.id}`, payload, authConfig());
      setSelectedArticle(response.data);
      setForm(makeForm(response.data));
      toast.success("Market insight draft updated");
      await loadArticles();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (path, successMessage) => {
    if (!selectedArticle) return;
    setSaving(true);
    try {
      const response = await api.post(path, {}, authConfig());
      setSelectedArticle(response.data);
      setForm(makeForm(response.data));
      toast.success(successMessage);
      await loadArticles();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[0.88fr_1.12fr]" data-testid="admin-market-insights-panel">
      <div className={`${panelClass} space-y-6`} data-testid="admin-market-insights-list-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Market Insights</p>
            <h2 className="mt-2 text-3xl font-semibold text-[#001935]">Editorial CMS</h2>
            <p className="mt-2 text-sm leading-7 text-[#42566f]" data-testid="admin-market-insights-helper-text">
              Review the latest URA and HDB releases, refine the AI draft, and publish only when it feels client-ready.
            </p>
          </div>
          <button className="gold-button" data-testid="admin-market-insights-sync-button" disabled={syncing} onClick={syncLatestReleases} type="button">
            <RefreshCcw className="mr-2 h-4 w-4" /> {syncing ? "Syncing latest releases..." : "Fetch latest 6 drafts"}
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-[24px] border border-[#D4AF37]/25 bg-[#fffaf1] p-4" data-testid="admin-market-insights-stat-total">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Total</p>
            <p className="mt-2 text-3xl font-semibold text-[#001935]">{draftStats.total}</p>
          </div>
          <div className="rounded-[24px] border border-[#D4AF37]/25 bg-[#fffaf1] p-4" data-testid="admin-market-insights-stat-drafts">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Drafts</p>
            <p className="mt-2 text-3xl font-semibold text-[#001935]">{draftStats.drafts}</p>
          </div>
          <div className="rounded-[24px] border border-[#D4AF37]/25 bg-[#fffaf1] p-4" data-testid="admin-market-insights-stat-published">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Published</p>
            <p className="mt-2 text-3xl font-semibold text-[#001935]">{draftStats.published}</p>
          </div>
        </div>

        <div className="space-y-4" data-testid="admin-market-insights-list">
          {loadingList ? (
            <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5 text-sm text-[#42566f]" data-testid="admin-market-insights-list-loading">
              Loading insight drafts...
            </div>
          ) : null}

          {!loadingList && !articles.length ? (
            <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5 text-sm leading-7 text-[#42566f]" data-testid="admin-market-insights-empty-state">
              No drafts yet. Tap <strong>Fetch latest 6 drafts</strong> to pull 3 URA and 3 HDB releases into the CMS.
            </div>
          ) : null}

          {articles.map((article) => (
            <button
              className={`w-full rounded-[26px] border p-5 text-left transition ${selectedId === article.id ? "border-[#D4AF37]/60 bg-[#fff6e4] shadow-sm" : "border-[#001935]/10 bg-white hover:border-[#D4AF37]/40 hover:bg-[#fffaf1]"}`}
              data-testid={`admin-market-insight-row-${article.id}`}
              key={article.id}
              onClick={() => setSelectedId(article.id)}
              type="button"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid={`admin-market-insight-source-${article.id}`}>
                    {article.source_name}
                  </p>
                  <h3 className="mt-2 text-xl font-semibold leading-tight text-[#001935]" data-testid={`admin-market-insight-title-${article.id}`}>
                    {article.title}
                  </h3>
                </div>
                <span
                  className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${article.status === "published" ? "bg-[#001935] text-[#F5D98C]" : "bg-[#F6EBC5] text-[#8b6d16]"}`}
                  data-testid={`admin-market-insight-status-${article.id}`}
                >
                  {article.status}
                </span>
              </div>
              <p className="mt-3 text-sm leading-7 text-[#42566f]" data-testid={`admin-market-insight-excerpt-${article.id}`}>
                {article.excerpt || "Fresh AI draft ready for editorial review."}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.16em] text-[#6b6252]">
                <span data-testid={`admin-market-insight-date-${article.id}`}>{formatDate(article.source_published_at)}</span>
                {article.district_code ? <span data-testid={`admin-market-insight-district-${article.id}`}>{article.district_code}</span> : null}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className={`${panelClass} space-y-6`} data-testid="admin-market-insights-editor-card">
        {loadingDetail ? (
          <div className="flex min-h-[420px] items-center justify-center rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] text-[#42566f]" data-testid="admin-market-insights-detail-loading">
            <LoaderCircle className="mr-3 h-5 w-5 animate-spin" /> Loading draft editor...
          </div>
        ) : null}

        {!loadingDetail && selectedArticle ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8b6d16]">Draft Editor</p>
                <h2 className="mt-2 text-3xl font-semibold text-[#001935]" data-testid="admin-market-insights-editor-heading">
                  {selectedArticle.raw_headline}
                </h2>
                <p className="mt-2 text-sm leading-7 text-[#42566f]" data-testid="admin-market-insights-editor-meta">
                  {selectedArticle.source_name} • generated {formatDate(selectedArticle.generated_at)} • updated {formatDate(selectedArticle.updated_at)}
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  className="outline-button"
                  data-testid="admin-market-insights-regenerate-button"
                  disabled={saving}
                  onClick={() => runAction(`/admin/market-insights/${selectedArticle.id}/regenerate`, "AI draft regenerated")}
                  type="button"
                >
                  <Sparkles className="mr-2 h-4 w-4" /> Regenerate
                </button>
                <button className="outline-button" data-testid="admin-market-insights-save-button" disabled={saving} onClick={saveChanges} type="button">
                  Save Draft
                </button>
                <button
                  className="gold-button"
                  data-testid="admin-market-insights-publish-button"
                  disabled={saving || selectedArticle.status === "published"}
                  onClick={() => runAction(`/admin/market-insights/${selectedArticle.id}/publish`, "Insight published to /insights")}
                  type="button"
                >
                  Publish
                </button>
              </div>
            </div>

            {selectedArticle.status === "published" ? (
              <Link className="inline-flex w-fit items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid="admin-market-insights-live-link" to={`/insights/${selectedArticle.slug || selectedArticle.id}`}>
                View live insight
              </Link>
            ) : null}

            <div className="grid gap-4 md:grid-cols-2">
              <input className={fieldClass} data-testid="admin-market-insights-title-input" onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="Insight title" value={form.title} />
              <input className={fieldClass} data-testid="admin-market-insights-image-input" onChange={(event) => setForm({ ...form, image_url: event.target.value })} placeholder="Hero image URL" value={form.image_url} />
            </div>

            <textarea className={`${fieldClass} min-h-24`} data-testid="admin-market-insights-excerpt-input" onChange={(event) => setForm({ ...form, excerpt: event.target.value })} placeholder="Executive teaser" value={form.excerpt} />
            <textarea className={`${fieldClass} min-h-28`} data-testid="admin-market-insights-summary-input" onChange={(event) => setForm({ ...form, executive_summary: event.target.value })} placeholder="Executive summary" value={form.executive_summary} />
            <textarea className={`${fieldClass} min-h-64`} data-testid="admin-market-insights-paragraphs-input" onChange={(event) => setForm({ ...form, paragraphs: event.target.value })} placeholder="One paragraph per block" value={form.paragraphs} />
            <textarea className={`${fieldClass} min-h-24`} data-testid="admin-market-insights-insight-points-input" onChange={(event) => setForm({ ...form, insight_points: event.target.value })} placeholder="One bullet point per line" value={form.insight_points} />
            <textarea className={`${fieldClass} min-h-20`} data-testid="admin-market-insights-takeaway-input" onChange={(event) => setForm({ ...form, takeaway: event.target.value })} placeholder="Key takeaway" value={form.takeaway} />
            <textarea className={`${fieldClass} min-h-24`} data-testid="admin-market-insights-advisory-input" onChange={(event) => setForm({ ...form, advisory: event.target.value })} placeholder="Practical advisory" value={form.advisory} />
            <textarea className={`${fieldClass} min-h-20`} data-testid="admin-market-insights-pull-quote-input" onChange={(event) => setForm({ ...form, pull_quote: event.target.value })} placeholder="Pull quote" value={form.pull_quote} />
            <textarea className={`${fieldClass} min-h-20`} data-testid="admin-market-insights-hero-caption-input" onChange={(event) => setForm({ ...form, hero_caption: event.target.value })} placeholder="Hero caption" value={form.hero_caption} />
            <textarea className={`${fieldClass} min-h-24`} data-testid="admin-market-insights-market-context-input" onChange={(event) => setForm({ ...form, market_context: event.target.value })} placeholder="2026 market context" value={form.market_context} />

            <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5" data-testid="admin-market-insights-source-text-card">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Raw Source Text</p>
              <p className="mt-4 whitespace-pre-wrap text-sm leading-7 text-[#42566f]" data-testid="admin-market-insights-source-text">
                {selectedArticle.raw_text || "No source body captured for this release."}
              </p>
            </div>
          </>
        ) : null}

        {!loadingDetail && !selectedArticle ? (
          <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-8 text-sm leading-7 text-[#42566f]" data-testid="admin-market-insights-editor-empty-state">
            Select a draft from the left to review and publish it.
          </div>
        ) : null}
      </div>
    </div>
  );
};